import { JSX, useContext, createSignal, Show, createEffect, on, createResource, Resource, ResourceReturn } from 'solid-js';
import { ContentHubCardType, ContentHubProps } from './content-hub-types';
import { Button } from '../../ui-components/button/button';
import { ErrorCatcher } from '../../tools/error-catcher';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Grid } from '../../grid-system/grid/grid';
import decodeHtmlEntities from '../../tools/decode-html-entities';
import { PostCardPlaceholder } from './post-card-placeholder';
import {
    GET_CUSTOM_POSTS_QUERY,
    EntriesShape,
    CustomPostsShape,
    buildEntriesQuery,
} from './query';
import { createFilters, getSlug } from './filters';
import { createCardHelpers } from './cards';

const NUMBER_OF_CARDS = 2;

/** Loose attribute shape from the WP `atos/content-hub` block. Old posts may
 *  still carry removed fields (card3, link1, eyebrowHeading); they're ignored. */
type ContentHubAttributes = {
    audience?: ContentHubProps['audience'];
    heading?: string;
    contentType?: ContentHubCardType;
    globalIncludeAndPreferOnDemandEvents?: boolean;
    card1?: ContentHubCardType;
    card2?: ContentHubCardType;
    ctaLabel?: string;
    ctaUrl?: string | { url?: string };
};

export const ContentHub = (props: ContentHubProps) => {
    const { createCachedResource, localize } = useContext(AppContext);

    const [err, setErr] = createSignal(false);
    const [renderedCards, setRenderedCards] = createSignal<JSX.Element[]>(
        Array.from({ length: NUMBER_OF_CARDS }, () => <PostCardPlaceholder />),
    );

    if (props.mixedContent && props.cards.length === 0) {
        return null;
    }

    const { query, variables, hasEntries } = buildEntriesQuery(props);
    // Skip the entries query entirely when every card is a custom post link.
    // Firing an empty query gets a 403 from the API gateway and crashes SSR.
    // Those cards are served by GET_CUSTOM_POSTS_QUERY below instead.
    let entries: Resource<EntriesShape | undefined>;
    if (hasEntries) {
        [entries] = createCachedResource(query, variables, true) as ResourceReturn<EntriesShape | undefined>;
    } else {
        [entries] = createResource(() => ({} as EntriesShape));
    }

    const customPostLinks = props.cards
        .map((c) => c.customPostLink?.url)
        .filter(Boolean) as string[];

    let customPostLinkEntries: (() => CustomPostsShape | undefined) | undefined;
    if (customPostLinks.length) {
        [customPostLinkEntries] = createCachedResource(
            GET_CUSTOM_POSTS_QUERY,
            { keys: customPostLinks },
            true,
        );
    }

    const filters = createFilters(props);
    const cardHelpers = createCardHelpers({
        props,
        localize,
        entries: () => entries() as EntriesShape | undefined,
        customPostLinkEntries,
        filters,
    });

    createEffect(
        on(
            () => [entries(), customPostLinkEntries?.()],
            () => {
                if (entries.error) {
                    setErr(true);
                    return;
                }
                if (entries() === undefined) {
                    return;
                }

                const cards: JSX.Element[] = [];
                const renderedSlugs: string[] = [];

                const renderAt = (i: number) => {
                    const card = props.cards[i];
                    const customLink = card?.customPostLink;

                    if (customLink?.url && !renderedSlugs.includes(customLink.url)) {
                        renderedSlugs.push(customLink.url);
                        cards.push(cardHelpers.renderCustomLinkCard(customLink, card.posttype.value));
                        return;
                    }

                    const { type, tag } = cardHelpers.resolveTypeAndTag(i);

                    // Don't leave empty slots — relax the tag requirement and retry.
                    const post = cardHelpers.parseCardsData(i, renderedSlugs, type, tag)
                        ?? cardHelpers.parseCardsData(i, renderedSlugs, type, 'NO_TAG');
                    if (!post) {
                        return;
                    }

                    const slug = getSlug(post);
                    if (slug && !renderedSlugs.includes(slug)) {
                        renderedSlugs.push(slug);
                    }
                    cards.push(cardHelpers.renderCard(post, type));
                };

                for (let i = 0; i < NUMBER_OF_CARDS; i++) {
                    renderAt(i);
                }

                // Hoist next card up if a slot came back empty
                if (cards.length < NUMBER_OF_CARDS) {
                    for (let i = cards.length; i < NUMBER_OF_CARDS; i++) {
                        renderAt(i);
                    }
                }

                setRenderedCards(cards);
            },
        ),
    );

    const ctaButton = () => {
        if (!props.ctaLabel || !props.ctaUrl) {
            return null;
        }
        return (
            <Button
                label={props.ctaLabel}
                url={props.ctaUrl}
                isLink={true}
                variant="primary"
                iconEnd="arrow-right"
            />
        );
    };

    return (
        <ErrorCatcher componentName="Content hub">
            <Show
                when={!err()}
                fallback={
                    <p class="body-md font-cvi-regular text-cvi-fg-default">
                        {localize('error-has-occurred', 'An error has occurred, please try again later')}
                    </p>
                }
            >
                <div class="py-cvi-section-spacious flex flex-col gap-cvi-10">
                    <Show
                        when={props.heading || (props.ctaUrl && props.ctaLabel)}
                        fallback={
                            <Grid templateShorthand={[6, 6]} responsive={{ tablet: [12], mobile: [12] }}>
                                <Show when={renderedCards()[0]}>{renderedCards()[0]}</Show>
                                <Show when={renderedCards()[1]}>{renderedCards()[1]}</Show>
                            </Grid>
                        }
                    >
                        <Grid templateShorthand={[4, 4, 4]} responsive={{ tablet: [12], mobile: [12] }}>
                            <div class="flex flex-col gap-cvi-10 desktop-small:max-w-[424px]">
                                <Show when={props.heading}>
                                    <h2 class="heading-3xl font-cvi-medium text-cvi-fg-default m-0">
                                        {decodeHtmlEntities(props.heading)}
                                    </h2>
                                </Show>
                                <Show when={props.ctaUrl && props.ctaLabel}>
                                    <div class="hidden desktop-small:block">{ctaButton()}</div>
                                </Show>
                            </div>
                            <Show when={renderedCards()[0]}>{renderedCards()[0]}</Show>
                            <Show when={renderedCards()[1]}>{renderedCards()[1]}</Show>
                        </Grid>
                    </Show>

                    <Show when={props.ctaUrl && props.ctaLabel}>
                        <div class="desktop-small:hidden">{ctaButton()}</div>
                    </Show>
                </div>
            </Show>
        </ErrorCatcher>
    );
};

ContentHub.parseProps = (atts: ContentHubAttributes): ContentHubProps => {
    // posttype.value is typed as the per-card union but block-level can also be 'mix'.
    const type: string | undefined = atts.contentType?.posttype?.value;
    const mixedContent = type === 'mix';
    // Only the first 2 cards render — card3 (if present on legacy posts) is ignored.
    const cards: (ContentHubCardType | undefined)[] = mixedContent
        ? [atts.card1, atts.card2]
        : [];

    const ctaUrl = typeof atts.ctaUrl === 'string' ? atts.ctaUrl : atts.ctaUrl?.url;

    return {
        heading: atts.heading ?? '',
        mixedContent,
        contentType: atts.contentType as ContentHubCardType,
        cards: cards.filter((card): card is ContentHubCardType => Boolean(card?.posttype?.value)),
        ctaLabel: atts.ctaLabel,
        ctaUrl,
        audience: atts.audience,
        globalIncludeAndPreferOnDemandEvents: atts.globalIncludeAndPreferOnDemandEvents ?? false,
    };
};
